import type { AxiosInstance } from 'axios';
import { CyclingEvent, EventCategory, CityInfo, EventMapPoint, PlaceSuggestion, CreateEventRequest, UpdateEventRequest, PageResponse, EventStatus, EventStatusFilter, EventEditResponse, ProposeEventRequest, ReviewEditRequest, AuditLogResponse, EntityActivityResponse, EventSourceResponse, UpdateEventSourceRequest, SourceSyncResult, DuplicateGroup, MergeEventsRequest, DismissDuplicatesRequest, EnqueueEnrichmentRequest, EnrichmentRequestResponse, AgentStateAdminRow, AgentStateSummary, AgentRunView, EnrichmentAttemptView, AgentHealthRow, EventOriginStats, EnrichmentCoverageStat, FeedbackSubmission, Feedback, FeedbackStats, FeedbackPlatform, WeatherForecast, SetEventPromotionRequest, EventDetailStats, NotifyCount, NotifyPreview, EventEdition, RegistrationAlertOrigin } from './types';

export function createEventService(api: AxiosInstance) {
  return {
    // dateTo: Ende eines Zeitraums („bis", inklusiv) — zusammen mit `date` („ab").
    // noCoordinates: nur Events ohne Koordinate — sie fehlen auf der Umkreis-Karte. Admin-Filter
    // der Event-Übersicht; öffentlich hätte er keinen Sinn.
    // status: 'ALL' fragt online + offline + archiviert in einer Liste ab (Redaktion). Es geht als
    // eigener Schalter `allStatuses` auf die Leitung, weil `status` serverseitig typisiert an das
    // Enum gebunden ist. Ohne Redaktionsrolle ignoriert der Server beides.
    getAll: async (page: number = 0, size: number = 20, status?: EventStatusFilter, bundesland?: string, lat?: number, lng?: number, radius?: number, search?: string, date?: string, minKm?: number, maxKm?: number, organizerId?: number, category?: string, city?: string, eventType?: string, noOrganizer?: boolean, sortBy?: string, sortDir?: 'asc' | 'desc', noEventType?: boolean, includePast?: boolean, country?: string, noCoordinates?: boolean, dateTo?: string, permanent?: boolean, includeOffSeason?: boolean, friendsOnly?: boolean): Promise<PageResponse<CyclingEvent>> => {
      const params: Record<string, unknown> = { page, size };
      if (status === 'ALL') params.allStatuses = true;
      else if (status) params.status = status;
      if (bundesland) params.bundesland = bundesland;
      if (lat != null) params.lat = lat;
      if (lng != null) params.lng = lng;
      if (radius != null) params.radius = radius;
      if (search && search.trim()) params.search = search.trim();
      if (date) params.date = date;
      if (dateTo) params.dateTo = dateTo;
      if (minKm != null) params.minKm = minKm;
      if (maxKm != null) params.maxKm = maxKm;
      if (organizerId != null) params.organizerId = organizerId;
      if (category) params.category = category;
      if (city) params.city = city;
      if (eventType) params.eventType = eventType;
      if (noEventType) params.noEventType = true;
      if (noOrganizer) params.noOrganizer = true;
      if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir ?? 'asc'; }
      if (includePast) params.includePast = true;
      if (country) params.country = country;
      if (noCoordinates) params.noCoordinates = true;
      // Ohne den Parameter liefert der Server öffentlich nur Termine; `true` nur permanente Events,
      // und zwar nur die, die gerade fahrbar sind — Saisonpause erst mit `includeOffSeason`.
      if (permanent != null) params.permanent = permanent;
      if (includeOffSeason) params.includeOffSeason = true;
      if (friendsOnly) params.friendsOnly = true;
      const response = await api.get('/events', { params });
      return response.data;
    },

    // Admin/Moderator: schreibt ein (i.d.R. vergangenes) Event auf die nächste Jahres-Edition
    // fort (Datum +years, URL/ID bleiben → Ranking-Erhalt). Wirft bei 409, wenn die Ziel-Edition
    // bereits als eigenes Event existiert.
    rollForward: async (id: number, years: number = 1): Promise<CyclingEvent> => {
      const response = await api.post(`/events/${id}/roll-forward`, null, { params: { years } });
      return response.data;
    },

    getCities: async (): Promise<CityInfo[]> => {
      const response = await api.get('/events/cities');
      return response.data;
    },

    /**
     * Kartenpunkte aller kommenden Events (id/Titel/Koordinate/Kategorie) für die Umkreis-Karte.
     * Bewusst ungefiltert: die Karte zeigt jeden Pin, färbt aber nur die im Radius ein — dafür
     * braucht sie den vollen Bestand, nicht die aktuell gefilterte Seite.
     *
     * `permanent = true` liefert statt der Termine die permanenten Events (Liste „Permanent"),
     * mit Gebiet und Ausdehnung, wo es keinen Startort gibt.
     */
    getMapPoints: async (permanent = false): Promise<EventMapPoint[]> => {
      const response = await api.get('/events/map-points', permanent ? { params: { permanent: true } } : undefined);
      return response.data;
    },

    /** Ortsvorschläge (Prefix, nach Bekanntheit) für die Umkreis-Suche. Unter 2 Zeichen leer. */
    searchPlaces: async (query: string, limit = 8): Promise<PlaceSuggestion[]> => {
      const q = query.trim();
      if (q.length < 2) return [];
      const response = await api.get('/geocode/places', { params: { q, limit } });
      return response.data;
    },

    /** PLZ → Koordinate. Null, wenn die PLZ unbekannt ist. */
    geocodePlz: async (plz: string, country = 'DE'): Promise<{ lat: number; lng: number } | null> => {
      try {
        const response = await api.get(`/geocode/plz/${plz}`, { params: { country } });
        return response.data;
      } catch {
        return null;
      }
    },

    findSimilar: async (title: string, eventDate: string, bundesland: string, excludeId?: number, category?: EventCategory): Promise<CyclingEvent[]> => {
      const params: Record<string, unknown> = { title, eventDate, bundesland };
      if (excludeId != null) params.excludeId = excludeId;
      if (category != null) params.category = category;
      const response = await api.get('/events/find-similar', { params });
      return response.data;
    },

    /**
     * Dasselbe Event in einem anderen Jahr — der Kandidat für „das ist die nächste Auflage".
     * Getrennt von `findSimilar`, weil ein Duplikat eine Warnung ist und eine frühere Auflage
     * ein Angebot: den neuen Termin dort eintragen, statt ein zweites Event anzulegen.
     */
    findEditions: async (title: string, eventDate: string, bundesland: string, excludeId?: number, category?: EventCategory): Promise<CyclingEvent[]> => {
      const params: Record<string, unknown> = { title, eventDate, bundesland };
      if (excludeId != null) params.excludeId = excludeId;
      if (category != null) params.category = category;
      const response = await api.get('/events/find-editions', { params });
      return response.data;
    },

    getBundeslaender: async (): Promise<string[]> => {
      const response = await api.get('/events/bundeslaender');
      return response.data;
    },

    getById: async (id: number): Promise<CyclingEvent> => {
      const response = await api.get(`/events/${id}`);
      return response.data;
    },

    /** Zahlen zur Event-Seite — Admin/Moderator oder Premium-Verwalter des Veranstalters. */
    getStats: async (id: number): Promise<EventDetailStats> => {
      const response = await api.get(`/events/${id}/stats`);
      return response.data;
    },

    /** Frühere Auflagen aller sichtbaren Events eines Veranstalters, älteste zuerst. */
    getOrganizerEditions: async (organizerId: number): Promise<EventEdition[]> => {
      const response = await api.get(`/organizers/${organizerId}/editions`);
      return response.data;
    },

    /** Wettervorhersage für den Event-Tag (DWD). Null, wenn keine Prognose verfügbar ist (>7 Tage, keine Koordinaten). */
    getWeather: async (id: number): Promise<WeatherForecast | null> => {
      const response = await api.get(`/events/${id}/weather`);
      return response.data || null;
    },

    create: async (data: CreateEventRequest): Promise<CyclingEvent> => {
      const response = await api.post('/events', data);
      return response.data;
    },

    /**
     * @param notifyFavorites Wenn true, bekommen die Favoriten-Besitzer eine Mail zu den
     *   geänderten Feldern. Entscheidet der Admin/Moderator im Bestätigungsdialog beim Speichern.
     */
    update: async (id: number, data: UpdateEventRequest, notifyFavorites = false): Promise<CyclingEvent> => {
      const response = await api.put(`/events/${id}`, data, { params: { notifyFavorites } });
      return response.data;
    },

    /**
     * Wie [getNotifyCount], aber für einen konkreten Formularstand: zählt nur, wenn das Speichern
     * von `data` überhaupt eine Mail auslösen würde. Schreibt nichts.
     */
    getNotifyPreview: async (id: number, data: UpdateEventRequest): Promise<NotifyPreview> => {
      const response = await api.post(`/events/${id}/notify-preview`, data);
      return {
        count: response.data?.count ?? 0,
        alreadyNotifiedToday: response.data?.alreadyNotifiedToday === true,
        wouldNotify: response.data?.wouldNotify === true,
      };
    },

    /**
     * Wie viele Nutzer eine Änderung an diesen Events benachrichtigen würde. Speist den
     * Bestätigungsdialog vor dem Speichern — bei 0 wird er nicht angezeigt.
     * Mehrere Ids braucht das Zusammenführen: dort zählen auch die Favoriten der Duplikate mit.
     */
    getNotifyCount: async (eventIds: number[]): Promise<NotifyCount> => {
      if (eventIds.length === 0) return { count: 0, alreadyNotifiedToday: false };
      const response = await api.get('/events/notify-count', {
        params: { eventIds: eventIds.join(',') },
      });
      return {
        count: response.data?.count ?? 0,
        alreadyNotifiedToday: response.data?.alreadyNotifiedToday === true,
      };
    },

    updateStatus: async (id: number, status: EventStatus): Promise<CyclingEvent> => {
      const response = await api.patch(`/events/${id}/status`, null, { params: { status } });
      return response.data;
    },

    /**
     * Admin/Moderator: bezahlte Hervorhebung der Card setzen bzw. ersetzen. Die Laufzeit beginnt
     * sofort; ihr Ende kommt entweder aus `days` oder aus `untilEvent` (Ende der Veranstaltung).
     */
    setPromotion: async (id: number, data: SetEventPromotionRequest): Promise<CyclingEvent> => {
      const response = await api.put(`/events/${id}/promotion`, data);
      return response.data;
    },

    /** Admin/Moderator: Hervorhebung sofort zurücknehmen. */
    clearPromotion: async (id: number): Promise<CyclingEvent> => {
      const response = await api.delete(`/events/${id}/promotion`);
      return response.data;
    },

    triggerScrape: async (): Promise<{ message: string }> => {
      const response = await api.post('/events/scrape');
      return response.data;
    },

    triggerStravaSync: async (): Promise<{ message: string; imported: number }> => {
      const response = await api.post('/events/sync-strava');
      return response.data;
    },

    triggerGCTourenSync: async (): Promise<{ message: string; imported: number }> => {
      const response = await api.post('/events/sync-gctouren');
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await api.delete(`/events/${id}`);
    },

    /** Glocke: Event vormerken und die Anmeldungs-Nachricht einschalten (siehe registrationAlert.ts). */
    enableRegistrationAlert: async (
      eventId: number,
      /** Nur für die Zählung unter `/admin/geschaeft` (V183). */
      origin?: RegistrationAlertOrigin,
    ): Promise<{ favorited: boolean; registrationEnabled: boolean }> => {
      const res = await api.post(`/events/${eventId}/registration-alert`, origin ?? {});
      return res.data;
    },

    addFavorite: async (eventId: number): Promise<void> => {
      await api.post(`/events/${eventId}/favorite`);
    },

    removeFavorite: async (eventId: number): Promise<void> => {
      await api.delete(`/events/${eventId}/favorite`);
    },

    getMyFavorites: async (): Promise<CyclingEvent[]> => {
      const response = await api.get('/events/favorites');
      return response.data;
    },

    /**
     * Nur die Ids der eigenen Favoriten — für die Umkreis-Karte, die favorisierte Events
     * hervorhebt und dabei *alle* kommenden Events zeigt, nicht nur die geladene Listenseite.
     */
    getFavoriteIds: async (): Promise<number[]> => {
      const response = await api.get('/events/favorites/ids');
      return response.data;
    },

    // Event edit proposals
    proposeNewEvent: async (data: ProposeEventRequest): Promise<EventEditResponse> => {
      const response = await api.post('/event-edits/propose', data);
      return response.data;
    },

    proposeEdit: async (eventId: number, data: ProposeEventRequest): Promise<EventEditResponse> => {
      const response = await api.post(`/event-edits/propose/${eventId}`, data);
      return response.data;
    },

    /**
     * Foto zu einem gerade eingereichten Vorschlag. Zweiter Schritt nach proposeEdit/
     * proposeNewEvent — das Bild braucht die ID des Vorschlags. Das Backend nimmt es nur
     * kurz nach dem Einreichen und nur einmal an.
     */
    uploadProposalImage: async (editId: number, file: File | Blob): Promise<void> => {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/event-edits/${editId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    /**
     * Flyer zu einem gerade eingereichten Vorschlag — zweiter Schritt wie beim Foto, aus
     * demselben Grund: die Datei braucht die ID des Vorschlags.
     *
     * Antwortet der Server mit **402**, gehört die Leistung zu Premium und ist für diesen
     * Veranstalter nicht gebucht. Kein 403 — das behandeln die Interceptors wie 401.
     */
    uploadProposalFlyer: async (editId: number, file: File | Blob): Promise<void> => {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/event-edits/${editId}/flyer`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    /**
     * GPX-Datei zu einer vorgeschlagenen Distanz. Wie das Foto ein zweiter Schritt nach
     * proposeEdit/proposeNewEvent: `distanceId` ist die Id aus `EventEditResponse.distances`,
     * die es vorher noch nicht gibt. Das Backend nimmt die Datei nur kurz nach dem Einreichen
     * und nur einmal je Distanz an.
     */
    uploadProposalGpx: async (editId: number, distanceId: number, file: File | Blob): Promise<void> => {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/event-edits/${editId}/distances/${distanceId}/gpx`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    /**
     * Meldet nach dem letzten Upload, dass alle Anhänge des eigenen Vorschlags oben sind. Darf der
     * Veranstalter ohne Prüfung ändern, ist der Vorschlag danach freigegeben (`status`
     * `APPROVED`), sonst bleibt er offen. Nur für angemeldete Konten.
     */
    finishProposalAttachments: async (editId: number): Promise<EventEditResponse> => {
      const response = await api.post(`/event-edits/${editId}/attachments-done`);
      return response.data;
    },

    /**
     * Der eigene offene Vorschlag zu einem Event, oder null. Das Formular setzt dort an statt beim
     * Event, und der neue Vorschlag ersetzt ihn (siehe EventEditService.supersedeOwnOpenEdits).
     */
    getMyOpenEdit: async (eventId: number): Promise<EventEditResponse | null> => {
      const response = await api.get(`/event-edits/my-open/${eventId}`);
      return response.status === 204 ? null : (response.data || null);
    },

    getMySubmissions: async (): Promise<EventEditResponse[]> => {
      const response = await api.get('/event-edits/my-submissions');
      return response.data;
    },

    // sortBy: 'title' | 'bundesland' | 'eventDate' | 'type' | 'createdAt' (Default: createdAt, neueste zuerst)
    getPendingEdits: async (
      page: number = 0,
      size: number = 20,
      sortBy?: string,
      sortDir?: 'asc' | 'desc',
    ): Promise<PageResponse<EventEditResponse>> => {
      const params: Record<string, unknown> = { page, size };
      if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir ?? 'asc'; }
      else if (sortDir) { params.sortDir = sortDir; }
      const response = await api.get('/event-edits/pending', { params });
      return response.data;
    },

    getEditDetail: async (id: number): Promise<EventEditResponse> => {
      const response = await api.get(`/event-edits/${id}`);
      return response.data;
    },

    reviewEdit: async (id: number, data: ReviewEditRequest): Promise<EventEditResponse> => {
      const response = await api.post(`/event-edits/${id}/review`, data);
      return response.data;
    },

    approveAllPendingEdits: async (comment?: string): Promise<void> => {
      await api.post('/event-edits/approve-all-pending', { comment });
    },

    rejectAllPendingEdits: async (comment?: string): Promise<void> => {
      await api.post('/event-edits/reject-all-pending', { comment });
    },

    // Feedback widget
    submitFeedback: async (data: FeedbackSubmission): Promise<void> => {
      await api.post('/feedback', data);
    },

    getFeedback: async (
      page: number = 0,
      size: number = 20,
      filter?: { platform?: FeedbackPlatform; minRating?: number; withComment?: boolean }
    ): Promise<PageResponse<Feedback>> => {
      const params: Record<string, unknown> = { page, size };
      if (filter?.platform) params.platform = filter.platform;
      if (filter?.minRating != null) params.minRating = filter.minRating;
      if (filter?.withComment) params.withComment = true;
      const response = await api.get('/feedback', { params });
      return response.data;
    },

    getFeedbackStats: async (): Promise<FeedbackStats> => {
      const response = await api.get('/feedback/stats');
      return response.data;
    },

    // Audit logs
    getAuditLogs: async (
      page: number = 0,
      size: number = 20,
      filters: { action?: string; search?: string; actor?: string; userId?: number; source?: string; entityType?: string; entityId?: number; from?: string; to?: string } = {}
    ): Promise<PageResponse<AuditLogResponse>> => {
      const params: Record<string, unknown> = { page, size, ...filters };
      Object.keys(params).forEach(k => { if ((params as any)[k] === undefined || (params as any)[k] === '') delete (params as any)[k]; });
      const response = await api.get('/audit-logs', { params });
      return response.data;
    },

    getEntityActivity: async (
      page: number = 0,
      size: number = 20,
      filters: { action?: string; search?: string; actor?: string; source?: string; entityType?: string; from?: string; to?: string } = {}
    ): Promise<PageResponse<EntityActivityResponse>> => {
      const params: Record<string, unknown> = { page, size, ...filters };
      Object.keys(params).forEach(k => { if ((params as any)[k] === undefined || (params as any)[k] === '') delete (params as any)[k]; });
      const response = await api.get('/audit-logs/by-event', { params });
      return response.data;
    },

    getEntityAuditLogs: async (entityType: string, entityId: number): Promise<AuditLogResponse[]> => {
      const response = await api.get(`/audit-logs/entity/${entityType}/${entityId}`);
      return response.data;
    },

    // Change history for a single event (Admin + Moderator), newest first.
    getEventHistory: async (eventId: number): Promise<AuditLogResponse[]> => {
      const response = await api.get(`/events/${eventId}/history`);
      return response.data;
    },

    // Enrichment queue
    enqueueEnrichment: async (body: EnqueueEnrichmentRequest): Promise<{ requestId: number }> => {
      const response = await api.post('/enrichment-requests', body);
      return response.data;
    },

    getEnrichmentQueueCounts: async (): Promise<{ countsBySource: Record<string, number> }> => {
      const response = await api.get('/enrichment-requests');
      return response.data;
    },

    getEnrichmentQueueBySource: async (source: string): Promise<{ items: EnrichmentRequestResponse[] }> => {
      const response = await api.get('/enrichment-requests', { params: { source } });
      return response.data;
    },

    // Agent enrichment state (cooldown tracking)
    getAgentStateSummary: async (): Promise<AgentStateSummary[]> => {
      const response = await api.get('/admin/agent-state/summary');
      return response.data;
    },

    getAgentState: async (params: { source?: string; entityType?: string } = {}): Promise<AgentStateAdminRow[]> => {
      const response = await api.get('/admin/agent-state', {
        params: {
          ...(params.source ? { source: params.source } : {}),
          ...(params.entityType ? { entity_type: params.entityType } : {}),
        },
      });
      return response.data;
    },

    resetAgentState: async (id: number): Promise<void> => {
      await api.delete(`/admin/agent-state/${id}`);
    },

    // Agent observability (runs + attempts + health)
    getAgentRuns: async (params: { source?: string; limit?: number } = {}): Promise<AgentRunView[]> => {
      const response = await api.get('/admin/agent-runs', {
        params: {
          ...(params.source ? { source: params.source } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
        },
      });
      return response.data;
    },

    getAgentRunAttempts: async (runId: number): Promise<EnrichmentAttemptView[]> => {
      const response = await api.get(`/admin/agent-runs/${runId}/attempts`);
      return response.data;
    },

    getAgentHealth: async (): Promise<AgentHealthRow[]> => {
      const response = await api.get('/admin/agent-health');
      return response.data;
    },

    // Admin statistics
    getEventOriginStats: async (): Promise<EventOriginStats> => {
      const response = await api.get('/admin/stats/event-origins');
      return response.data;
    },

    getEnrichmentCoverage: async (source?: string): Promise<EnrichmentCoverageStat[]> => {
      const response = await api.get('/admin/stats/enrichment-coverage', {
        params: source ? { source } : {},
      });
      return response.data;
    },

    // Event sources
    getEventSources: async (): Promise<EventSourceResponse[]> => {
      const response = await api.get('/event-sources');
      return response.data;
    },

    updateEventSource: async (id: number, data: UpdateEventSourceRequest): Promise<EventSourceResponse> => {
      const response = await api.put(`/event-sources/${id}`, data);
      return response.data;
    },

    syncAll: async (): Promise<SourceSyncResult[]> => {
      const response = await api.post('/sync/run-all');
      return response.data;
    },

    syncSource: async (sourceKey: string): Promise<SourceSyncResult> => {
      const response = await api.post(`/sync/run/${sourceKey}`);
      return response.data;
    },

    // Duplicates
    getDuplicates: async (): Promise<DuplicateGroup[]> => {
      const response = await api.get('/events/duplicates');
      return response.data;
    },

    mergeEvents: async (req: MergeEventsRequest, notifyFavorites = false): Promise<CyclingEvent> => {
      const response = await api.post('/events/merge', req, { params: { notifyFavorites } });
      return response.data;
    },

    dismissDuplicates: async (req: DismissDuplicatesRequest): Promise<void> => {
      await api.post('/events/duplicates/dismiss', req);
    },
  };
}

export type EventService = ReturnType<typeof createEventService>;
